import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';
import { apiFetch } from '@/services/apiClient';
import {
  Shield, Plus, Copy, Check, RefreshCw, Search, Wallet, Gift,
  CircleDashed, CheckCircle2, KeyRound,
} from 'lucide-react';

interface CdKey {
  code: string;
  amount: number;
  status: 'unused' | 'used';
  used_by: string | null;
  used_at: string | null;
  created_by: string;
  note: string;
  createdAt: string;
}

export function AdminCenter({ lang }: { t?: any; lang: 'en' | 'zh' }) {
  const isZh = lang === 'zh';
  const ui = (zh: string, en: string) => (isZh ? zh : en);

  const [count, setCount] = useState('1');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [generating, setGenerating] = useState(false);

  const [cdkeys, setCdkeys] = useState<CdKey[]>([]);
  const [loadingList, setLoadingList] = useState(false);

  const [filter, setFilter] = useState<'all' | 'unused' | 'used'>('all');
  const [search, setSearch] = useState('');

  const [lastGenerated, setLastGenerated] = useState<string[]>([]);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await apiFetch('/api/admin/cdkeys', { method: 'GET' });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || '加载失败');
      setCdkeys(Array.isArray(data.cdkeys) ? data.cdkeys : []);
    } catch (err: unknown) {
      toast.error(ui('加载充值卡列表失败', 'Failed to load CDKey list') + ': ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoadingList(false);
    }
  }, [ui]);

  useEffect(() => { loadList(); }, [loadList]);

  const handleGenerate = useCallback(async () => {
    const cnt = Math.min(Math.max(1, Math.floor(Number(count) || 1)), 100);
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error(ui('兑换金额必须大于 0', 'Amount must be greater than 0'));
      return;
    }
    setGenerating(true);
    try {
      const res = await apiFetch('/api/admin/cdkeys/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: cnt, amount: amt, note: note.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || '生成失败');
      setLastGenerated(Array.isArray(data.codes) ? data.codes : []);
      toast.success(ui(`成功生成 ${data.count} 张充值卡`, `Successfully generated ${data.count} CDKeys`));
      setAmount('');
      setNote('');
      await loadList();
    } catch (err: unknown) {
      toast.error(ui('生成失败', 'Generation failed') + ': ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setGenerating(false);
    }
  }, [count, amount, note, loadList, ui]);

  const copyText = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(ui('已复制', 'Copied'));
    } catch {
      toast.error(ui('复制失败', 'Copy failed'));
    }
  }, [ui]);

  const copyAll = useCallback(async () => {
    if (lastGenerated.length === 0) return;
    try {
      await navigator.clipboard.writeText(lastGenerated.join('\n'));
      toast.success(ui(`已复制 ${lastGenerated.length} 张卡密`, `Copied ${lastGenerated.length} codes`));
    } catch {
      toast.error(ui('复制失败', 'Copy failed'));
    }
  }, [lastGenerated, ui]);

  const stats = useMemo(() => {
    const total = cdkeys.length;
    const used = cdkeys.filter(c => c.status === 'used').length;
    const unused = total - used;
    const totalAmount = cdkeys.reduce((s, c) => s + (Number(c.amount) || 0), 0);
    const redeemedAmount = cdkeys.filter(c => c.status === 'used').reduce((s, c) => s + (Number(c.amount) || 0), 0);
    return { total, used, unused, totalAmount, redeemedAmount };
  }, [cdkeys]);

  const filtered = useMemo(() => {
    const kw = search.trim().toLowerCase();
    return cdkeys.filter(c => {
      if (filter !== 'all' && c.status !== filter) return false;
      if (kw && !c.code.toLowerCase().includes(kw)) return false;
      return true;
    });
  }, [cdkeys, filter, search]);

  const fmtTime = (iso: string | null) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString(isZh ? 'zh-CN' : 'en-US'); } catch { return iso; }
  };

  return (
    <div className="h-full overflow-y-auto custom-scrollbar p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center text-white shadow-lg">
          <Shield size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-black text-white tracking-tight">{ui('管理中心', 'Admin Center')}</h1>
          <p className="text-sm text-white/40">{ui('充值卡生成与核销管理', 'Generate and manage recharge cards')}</p>
        </div>
      </div>

      {/* Generate card */}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-5 space-y-4">
        <div className="flex items-center gap-2 text-white/80 font-semibold">
          <KeyRound size={18} className="text-celestial-saturn" />
          {ui('生成充值卡', 'Generate Recharge Card')}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-white/40">{ui('数量（1-100）', 'Count (1-100)')}</label>
            <input
              type="number" min={1} max={100} value={count}
              onChange={(e) => setCount(e.target.value)}
              className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-celestial-saturn/50"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-white/40">{ui('金额（余额增量）', 'Amount (balance added)')}</label>
            <input
              type="number" min={1} value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="100"
              className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-celestial-saturn/50"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-white/40">{ui('备注（可选）', 'Note (optional)')}</label>
            <input
              type="text" value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={ui('运营赠送', 'Promotional gift')}
              className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-celestial-saturn/50"
            />
          </div>
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-celestial-saturn text-black text-sm font-bold hover:opacity-90 disabled:opacity-50 transition-all"
        >
          {generating ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />}
          {generating ? ui('生成中…', 'Generating…') : ui('生成充值卡', 'Generate Card')}
        </button>
      </div>

      {/* Last generated result */}
      {lastGenerated.length > 0 && (
        <div className="rounded-2xl bg-celestial-saturn/10 border border-celestial-saturn/30 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-celestial-saturn font-semibold">
              <Gift size={18} />
              {ui('本次生成 ', 'Generated ')}{lastGenerated.length}{ui(' 张', ' cards')}
            </div>
            <button onClick={copyAll} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 text-xs text-white hover:bg-white/20 transition-all">
              <Copy size={14} /> {ui('复制全部', 'Copy All')}
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto custom-scrollbar">
            {lastGenerated.map((code, i) => (
              <div key={i} className="flex items-center justify-between gap-2 bg-black/30 border border-white/10 rounded-lg px-3 py-2">
                <code className="text-sm text-celestial-saturn font-mono">{code}</code>
                <button onClick={() => copyText(code)} className="text-white/50 hover:text-white transition-colors">
                  <Copy size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={<Gift size={16} />} label={ui('总卡数', 'Total')} value={String(stats.total)} />
        <StatCard icon={<CircleDashed size={16} />} label={ui('未使用', 'Unused')} value={String(stats.unused)} accent="text-emerald-400" />
        <StatCard icon={<CheckCircle2 size={16} />} label={ui('已使用', 'Used')} value={String(stats.used)} accent="text-sky-400" />
        <StatCard icon={<Wallet size={16} />} label={ui('发放总额', 'Issued')} value={stats.totalAmount.toLocaleString()} accent="text-celestial-saturn" />
        <StatCard icon={<Check size={16} />} label={ui('已兑换额', 'Redeemed')} value={stats.redeemedAmount.toLocaleString()} accent="text-rose-400" />
      </div>

      {/* List */}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${filter === 'all' ? 'bg-celestial-saturn text-black' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
            >{ui('全部', 'All')}</button>
            <button
              onClick={() => setFilter('unused')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${filter === 'unused' ? 'bg-celestial-saturn text-black' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
            >{ui('未使用', 'Unused')}</button>
            <button
              onClick={() => setFilter('used')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${filter === 'used' ? 'bg-celestial-saturn text-black' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
            >{ui('已使用', 'Used')}</button>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder={ui('搜索卡号', 'Search code')}
                className="bg-black/30 border border-white/10 rounded-xl pl-8 pr-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-celestial-saturn/50"
              />
            </div>
            <button onClick={loadList} disabled={loadingList}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 text-white/70 hover:bg-white/10 disabled:opacity-50 transition-all text-sm">
              <RefreshCw size={14} className={loadingList ? 'animate-spin' : ''} /> {ui('刷新', 'Refresh')}
            </button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center text-white/30 text-sm py-10">{ui('暂无充值卡', 'No CDKeys yet')}</div>
        ) : (
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/40 text-xs uppercase tracking-wider text-left">
                  <th className="px-3 py-2 font-medium">{ui('卡号', 'Code')}</th>
                  <th className="px-3 py-2 font-medium">{ui('金额', 'Amount')}</th>
                  <th className="px-3 py-2 font-medium">{ui('状态', 'Status')}</th>
                  <th className="px-3 py-2 font-medium">{ui('备注', 'Note')}</th>
                  <th className="px-3 py-2 font-medium">{ui('创建时间', 'Created')}</th>
                  <th className="px-3 py-2 font-medium">{ui('核销信息', 'Redeemed by')}</th>
                  <th className="px-3 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((c, i) => (
                  <tr key={i} className="text-white/80 hover:bg-white/5 transition-colors">
                    <td className="px-3 py-2 font-mono text-celestial-saturn">{c.code}</td>
                    <td className="px-3 py-2">{Number(c.amount).toLocaleString()}</td>
                    <td className="px-3 py-2">
                      {c.status === 'used' ? (
                        <span className="inline-flex items-center gap-1 text-sky-400"><CheckCircle2 size={14} />{ui('已使用', 'Used')}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-emerald-400"><CircleDashed size={14} />{ui('未使用', 'Unused')}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-white/50 max-w-[160px] truncate">{c.note || '-'}</td>
                    <td className="px-3 py-2 text-white/50">{fmtTime(c.createdAt)}</td>
                    <td className="px-3 py-2 text-white/50">
                      {c.status === 'used' ? (
                        <span>{c.used_by || '?'}<br /><span className="text-white/30">{fmtTime(c.used_at)}</span></span>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => copyText(c.code)} className="text-white/40 hover:text-white transition-colors">
                        <Copy size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-white/30">{ui('仅显示最近 200 条', 'Showing the latest 200 records')}</p>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, accent = 'text-white' }: { icon: ReactNode; label: string; value: string; accent?: string }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4 space-y-2">
      <div className={`flex items-center gap-1.5 text-xs text-white/40 ${accent}`}>{icon}{label}</div>
      <div className={`text-xl font-black ${accent}`}>{value}</div>
    </div>
  );
}
